import { useState } from "react";
import { toast } from "sonner";
import { api, apiError, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, ShieldCheck } from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";

export default function Account() {
  const { user, reload, can } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 6) return toast.error("Use at least 6 characters");
    if (form.new_password !== form.confirm) return toast.error("The two new passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password, new_password: form.new_password,
      });
      toast.success("Password changed");
      setForm({ current_password: "", new_password: "", confirm: "" });
      reload();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6" data-testid="account-page">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">My account</h1>
        <p className="mt-1 text-sm text-slate-600">Your sign-in details and role in the lab.</p>
      </div>

      <section className="border border-slate-200 p-4" data-testid="account-details">
        <h2 className="text-base font-semibold text-slate-900 md:text-lg">{user?.name}</h2>
        <p className="mt-1 text-sm text-slate-600">{user?.email}</p>
        <p className="mt-2 inline-flex items-center gap-2 bg-[#134e4a] px-2 py-0.5 text-[11px] font-semibold uppercase text-white">
          <ShieldCheck size={12} /> {user?.role_label || user?.role}
        </p>
        {user?.job_title && <p className="mt-2 text-sm text-slate-600">{user.job_title}</p>}
        {user?.last_login_at && (
          <p className="mt-2 text-xs text-slate-500">Last sign-in {fmtDateTime(user.last_login_at)}</p>
        )}
      </section>

      <section className="border border-slate-200 p-4" data-testid="appearance-preference">
        <h2 className="text-base font-semibold text-slate-900">Appearance</h2>
        <p className="mt-1 text-sm text-slate-600">
          Dark mode suits a dimmed sign-out room or a bench beside a microscope. Your choice is
          remembered on this device, and printed reports and labels always stay on white paper.
        </p>
        <ThemeToggle showLabel
                     className="mt-3 h-11 border border-slate-300 px-4 text-sm font-semibold text-slate-800 hover:bg-slate-50" />
      </section>

      {can("assistant") && (
      <section className="border border-slate-200 p-4" data-testid="briefing-preference">
        <h2 className="text-base font-semibold text-slate-900">Daily briefing</h2>
        <p className="mt-1 text-sm text-slate-600">
          Show a short briefing at the top of the dashboard once a day — new cases, STAT and turnaround
          status, incomplete cases, reagent lots expiring and instruments overdue. It only speaks when
          you press Play, so it stays quiet on a shared screen.
        </p>
        <label className="mt-3 flex items-start gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={!!user?.briefing_enabled} disabled={busy === "briefing"}
                 data-testid="briefing-preference-checkbox" className="mt-0.5 h-4 w-4"
                 onChange={async (e) => {
                   setBusy("briefing");
                   try {
                     await api.put("/ai/briefing/preference", { enabled: e.target.checked });
                     toast.success(e.target.checked ? "Daily briefing turned on" : "Daily briefing turned off");
                     reload();
                   } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
                 }} />
          <span>Show me the daily briefing on the dashboard</span>
        </label>
      </section>
      )}

      {can("assistant") && (
      <section className="border border-slate-200 p-4" data-testid="hands-free-preference">
        <h2 className="text-base font-semibold text-slate-900">Hands-free — “Silas”</h2>
        <p className="mt-1 text-sm text-slate-600">
          Listen for the name “Silas” anywhere in PathQity, then answer out loud — useful at the scope
          or grossing bench with gloves on. Your browser recognises speech on the machine and nothing
          is sent to PathQity until it hears the name. A panel shows whenever the microphone is live,
          with a mute button. Needs Chrome or Edge, and microphone permission.
        </p>
        <label className="mt-3 flex items-start gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={!!user?.hands_free_enabled} disabled={busy === "handsfree"}
                 data-testid="hands-free-checkbox" className="mt-0.5 h-4 w-4"
                 onChange={async (e) => {
                   setBusy("handsfree");
                   try {
                     await api.put("/ai/hands-free/preference", { enabled: e.target.checked });
                     toast.success(e.target.checked
                       ? "Hands-free is on — say “Silas” followed by your question"
                       : "Hands-free is off");
                     reload();
                   } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
                 }} />
          <span>Listen for “Silas” so I can ask hands-free</span>
        </label>
      </section>
      )}

      {user?.must_change_password && (
        <p className="border-l-2 border-amber-600 bg-amber-50 p-3 text-sm text-amber-900" data-testid="must-change-banner">
          You are still using the temporary password you were given. Please set your own below.
        </p>
      )}

      <form onSubmit={submit} className="space-y-3 border border-slate-200 p-4" data-testid="change-password-form">
        <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900 md:text-lg">
          <KeyRound size={16} className="text-[#134e4a]" /> Change password
        </h2>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-600">Current password</Label>
          <Input type="password" required value={form.current_password} data-testid="current-password-input"
                 onChange={(e) => setForm((f) => ({ ...f, current_password: e.target.value }))}
                 className="mt-1 h-11 border-slate-300" />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-600">New password</Label>
            <Input type="password" required minLength={6} value={form.new_password} data-testid="new-password-input"
                   onChange={(e) => setForm((f) => ({ ...f, new_password: e.target.value }))}
                   className="mt-1 h-11 border-slate-300" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-600">Confirm new password</Label>
            <Input type="password" required minLength={6} value={form.confirm} data-testid="confirm-password-input"
                   onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))}
                   className="mt-1 h-11 border-slate-300" />
          </div>
        </div>
        <Button type="submit" disabled={busy} data-testid="change-password-button"
                className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
          {busy ? "Saving…" : "Change password"}
        </Button>
      </form>
    </div>
  );
}
